import React, { useEffect, useState } from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { InventoryApiService } from "../../Service/InventoryApiService";
import { useHistory, useParams } from "react-router";
import { CommonService } from "../../Service/CommonService";
import { Tooltip } from "@mui/material";
import { IconButton, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search"
import DataGridLoader from "../Common/DataGridLoader";

interface LocationState {
  barrelNumber: string;
}

const ProofHistory: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const history = useHistory();
    const state = history.location.state as LocationState;
    const [proofData, setProofData] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [sortField, setSortField] = useState("updatedAt"); // Default sort
    const [sortDirection, setSortDirection] = useState("desc"); // Default ascending
    const [totalCount, setTotalCount] = useState(0);
    const [filter, setFilter] = useState<any>({
        proof: null,
        updatedAt: null,
        creadtedBy: null
    });

    useEffect(() => {
        getProofHistoryByBarrelNumber(page, pageSize, sortField, sortDirection, filter);
    }, []);

    const getProofHistoryByBarrelNumber = async (page, pageSize, sortField: any, sortDirection: any, filter: any) => {
      const controller = new AbortController();
      const signal = controller.signal;

        try {
            setIsLoading(true);
            const response = await InventoryApiService.getProofHistoryByBarrelNumber(state?.barrelNumber, page, pageSize, sortField, sortDirection, filter, signal).then((response) => {
                setProofData(response.data.content);
                setTotalCount(response.data.totalElements);
                setIsLoading(false);
            });
        } catch (error) {
            setIsLoading(false);
            console.error("Error fetching companies:", error);
        }
    };

    const onPaginationModelChange = (newPaginationModel) => {
        setPage(newPaginationModel.page);
        setPageSize(newPaginationModel.pageSize);

        getProofHistoryByBarrelNumber(newPaginationModel.page, newPaginationModel.pageSize, sortField, sortDirection, filter);
    };

    const handleSortChange = (sortModel) => {
        if (sortModel.length > 0) {
            const newSortField = sortModel[0].field;
            const newSortDirection = sortModel[0].sort; // "asc" or "desc" directly from DataGrid
            
            setSortField(newSortField);
            setSortDirection(newSortDirection);

            getProofHistoryByBarrelNumber(page, pageSize, newSortField, newSortDirection, filter);
            
        }
    };

    const CellWithTooltip = ({ value }: { value: string | number }) => {
        return (
            <Tooltip arrow title={value?.toString()}>
                <span>{value?.toString()}</span>
            </Tooltip>
        );
    };

    const tableDataWithIds = proofData.map((rowData, index) => ({
        ...rowData,
    }));

    const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, field: keyof any) => {
        const value = event.target.value;

        // Prepare a temporary filter object with the updated field value
        const tempFilter = {
            ...filter,
            [field]: value,
        };

        // Update the filter state
        setFilter(tempFilter);

        getProofHistoryByBarrelNumber(page, pageSize, sortField, sortDirection, tempFilter);
    };

    const columns: GridColDef[] = [
        {
            field: "proof",
            headerName: "Proof Value",
            flex: 1,
            headerClassName: "table-header border-0 my-2 h-100",
            renderCell: (params) => <CellWithTooltip value={CommonService.formatToSingleDecimal(params.value)} />,
            renderHeader: (params) => (
                <div>
                    <Typography
                        variant="body2"
                        sx={{ color: "#6F6F6F", fontSize: "14px" }}
                    >
                        {params.colDef.headerName}
                    </Typography>
                    <div className="position-relative" style={{ width: 300 }}>
                        <input
                            type="text"
                            className="form-control border mt-1"
                            style={{ textIndent: "24px", boxShadow: "none" }}
                            placeholder="Search"
                            value={filter.proof}
                            defaultValue={filter.proof}
                            onChange={(event) => handleFilterChange(event, "proof")}
                            onClick={(event) => event.stopPropagation()}
                        />
                        <SearchIcon
                            sx={{
                                color: "#6F6F6F",
                                position: "absolute",
                                top: "50%",
                                left: "8px",
                                transform: "translateY(-50%)",
                            }}
                        />
                    </div>
                </div>
            )
        },
        {
            field: "updatedAt",
            headerName: "Updated At",
            headerClassName: "table-header border-0 my-2 h-100",
            flex: 1,
            renderCell: (params) => <CellWithTooltip value={params.value} />,
            valueGetter: (value: any, row: any) => {
                const dateTimeString = row.updatedAt ? row.updatedAt : null;

                //call the method which give the formatted date and time
                if (dateTimeString) return CommonService.formatDateTime(dateTimeString);
            },
            renderHeader: (params) => (
                <div>
                    <Typography
                        variant="body2"
                        sx={{ color: "#6F6F6F", fontSize: "14px" }}
                    >
                        {params.colDef.headerName}
                    </Typography>
                    <div className="position-relative" style={{ width: "300px" }}>
                        <input
                            type="date"
                            className="form-control border mt-1"
                            style={{ boxShadow: "none" }}
                            id="birthdate"
                            name="modifiedDate"
                            placeholder="MM/DD/YYYY"
                            value={filter.updatedAt}
                            defaultValue={filter.updatedAt}
                            onChange={(event) => handleFilterChange(event, "updatedAt")}
                            onClick={(event) => event.stopPropagation()}
                        />
                    </div>
                </div>
            )
        },
        {
            field: "updatedBy",
            headerName: "Updated By",
            headerClassName: "table-header border-0 my-2 h-100",
            flex: 1,
            renderCell: (params) => <CellWithTooltip value={params.value} />,
            renderHeader: (params) => (
                <div>
                    <Typography
                        variant="body2"
                        sx={{ color: "#6F6F6F", fontSize: "14px" }}
                    >
                        {params.colDef.headerName}
                    </Typography>
                    <div className="position-relative" style={{ width: 300 }}>
                        <input
                            type="text"
                            className="form-control border mt-1"
                            style={{ textIndent: "24px", boxShadow: "none" }}
                            placeholder="Search"
                            value={filter.updatedBy}
                            defaultValue={filter.updatedBy}
                            onChange={(event) => handleFilterChange(event, "updatedBy")}
                            onClick={(event) => event.stopPropagation()}
                        />
                        <SearchIcon
                            sx={{
                                color: "#6F6F6F",
                                position: "absolute",
                                top: "50%",
                                left: "8px",
                                transform: "translateY(-50%)",
                            }}
                        />
                    </div>
                </div>
            )
        }
    ];

    const routeBack = () => {
        history.goBack();
    };

    return (
        <>
            <div
                className="usersList" style={{ paddingBottom: CommonService.isAdmin() ? 47 : "" }}>
                <div className="usersList__header--wrapper d-flex flex-wrap align-items-center w-100">
                    <div className="d-flex align-items-center justify-content-between w-100 flex-wrap gap-2 position-relative">

                        <div className="d-flex align-items-center gap-2 flex-wrap left-wrapper">

                            {CommonService.isAdmin() && (
                                <>
                                    <div className="back__button" onClick={routeBack}>

                                        <IconButton
                                            aria-label="ArrowBackIcon"
                                            sx={{ backgroundColor: "#FFF" }}
                                        >
                                            <ArrowBackIcon sx={{ color: "#1C1B1F" }} />
                                        </IconButton>
                                    </div>

                                    <div className="header--title">
                                        <h1 className="title mb-0">
                                            Proofing History
                                        </h1>
                                    </div>
                                    <div className="divider mx-2"></div>
                                </>
                            )}
                            <div className="time__details me-4 me-xl-4 mb-0">
                                <Typography
                                    variant="h6"
                                    sx={{ color: "#333333", fontSize: "12px", fontWeight: 400 }}
                                >
                                    Container Id
                                </Typography>
                                <Typography
                                    variant="h3"
                                    sx={{ color: "#333333", fontSize: "18px", fontWeight: 700 }}
                                >
                                    {state?.barrelNumber}
                                </Typography>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="user-table-container">
                    <div className="userTable">
                        <DataGrid
                            rows={tableDataWithIds}
                            columns={columns}
                            className="data-grid"
                            initialState={{
                                pagination: {
                                    paginationModel: { page, pageSize },
                                },
                            }}
                            pagination
                            paginationModel={{ page, pageSize }}
                            onPaginationModelChange={onPaginationModelChange}
                            onSortModelChange={handleSortChange}
                            rowCount={totalCount}
                            paginationMode="server"
                            sortingMode="server"
                            pageSizeOptions={[10, 20, 50, 100]}
                            loading={isLoading}
                            slots={{
                                loadingOverlay: DataGridLoader,
                            }}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}
export default ProofHistory;